import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Accessibility, X, Type, Contrast, Eye, MousePointer2, RotateCcw, Check } from 'lucide-react';
export function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    highContrast: false,
    largeText: false,
    grayscale: false,
    highlightLinks: false,
    readableFont: false
  });
  // Apply settings to the document
  useEffect(() => {
    const root = document.documentElement;
    if (settings.highContrast) root.classList.add('high-contrast');else root.classList.remove('high-contrast');
    if (settings.largeText) root.classList.add('large-text');else root.classList.remove('large-text');
    if (settings.grayscale) root.classList.add('grayscale-mode');else root.classList.remove('grayscale-mode');
    if (settings.highlightLinks) root.classList.add('highlight-links');else root.classList.remove('highlight-links');
    if (settings.readableFont) root.classList.add('readable-font');else root.classList.remove('readable-font');
  }, [settings]);
  const toggleSetting = (key: keyof typeof settings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const resetSettings = () => {
    setSettings({
      highContrast: false,
      largeText: false,
      grayscale: false,
      highlightLinks: false,
      readableFont: false
    });
  };
  return <>
      {/* Floating Toggle Button */}
      <button onClick={() => setIsOpen(true)} className="fixed bottom-6 left-6 z-[100] bg-federal-blue text-white p-3 rounded-full shadow-2xl hover:bg-federal-blue/90 transition-all focus:ring-4 focus:ring-muted-gold outline-none group" aria-label="Open Accessibility Menu" data-id="element-28">
        <Accessibility className="w-6 h-6 group-hover:scale-110 transition-transform" data-id="element-29" />
      </button>

      <AnimatePresence data-id="element-30">
        {isOpen && <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-start p-4 sm:p-6" data-id="element-31">
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsOpen(false)} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" data-id="element-32" />

            {/* Menu Panel */}
            <motion.div initial={{
          opacity: 0,
          x: -20,
          scale: 0.95
        }} animate={{
          opacity: 1,
          x: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          x: -20,
          scale: 0.95
        }} className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200" data-id="element-33">
              {/* Header */}
              <div className="bg-federal-blue p-6 text-white flex items-center justify-between" data-id="element-34">
                <div className="flex items-center gap-3" data-id="element-35">
                  <Accessibility className="w-6 h-6 text-muted-gold" data-id="element-36" />
                  <h2 className="font-bold text-lg" data-id="element-37">Accessibility Tools</h2>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/10 rounded-full transition-colors" aria-label="Close Menu" data-id="element-38">
                  <X className="w-6 h-6" data-id="element-39" />
                </button>
              </div>

              {/* Options Grid */}
              <div className="p-6 space-y-4" data-id="element-40">
                <div className="grid grid-cols-1 gap-3" data-id="element-41">
                  <AccessibilityOption icon={Contrast} label="High Contrast" active={settings.highContrast} onClick={() => toggleSetting('highContrast')} data-id="element-42" />
                  <AccessibilityOption icon={Type} label="Large Text" active={settings.largeText} onClick={() => toggleSetting('largeText')} data-id="element-43" />
                  <AccessibilityOption icon={Eye} label="Grayscale" active={settings.grayscale} onClick={() => toggleSetting('grayscale')} data-id="element-44" />
                  <AccessibilityOption icon={MousePointer2} label="Highlight Links" active={settings.highlightLinks} onClick={() => toggleSetting('highlightLinks')} data-id="element-45" />
                  <AccessibilityOption icon={Type} label="Readable Font" active={settings.readableFont} onClick={() => toggleSetting('readableFont')} data-id="element-46" />
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-47">
                  <button onClick={resetSettings} className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-slate-500 hover:text-federal-blue transition-colors" data-id="element-48">
                    <RotateCcw className="w-4 h-4" data-id="element-49" />
                    Reset All Settings
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center" data-id="element-50">
                Codes & Cogs Compliance Suite
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}
function AccessibilityOption({
  icon: Icon,
  label,
  active,
  onClick
}: {
  icon: any;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${active ? 'border-federal-blue bg-federal-blue/5 text-federal-blue' : 'border-slate-100 hover:border-slate-200 text-slate-600'}`} data-id="element-51">
      <div className="flex items-center gap-3" data-id="element-52">
        <Icon className={`w-5 h-5 ${active ? 'text-federal-blue' : 'text-slate-400'}`} data-id="element-53" />
        <span className="font-bold text-sm" data-id="element-54">{label}</span>
      </div>
      {active && <Check className="w-5 h-5" data-id="element-55" />}
    </button>;
}